/**
 * Debug logging utility with category filtering support
 *
 * Usage:
 *   import { debugLog, debugWarn, debugError } from '@/utils/debug'
 *   debugLog('api', 'Fetching posts...', { cursor })
 *   debugWarn('cache', 'Cache miss for:', postId)
 *   debugError('auth', 'Authentication failed:', error)
 *
 * Categories can be enabled/disabled via localStorage:
 *   localStorage.setItem('DEBUG_CATEGORIES', JSON.stringify(['api', 'cache']))
 */

export type DebugCategory =
  | "api"
  | "db"
  | "sync"
  | "auth"
  | "ui"
  | "performance"
  | "navigation"
  | "websocket"
  | "cache"
  | "feed"
  | "workbox"
  | "general";

const ALL_CATEGORIES: DebugCategory[] = [
  "api",
  "db",
  "sync",
  "auth",
  "ui",
  "performance",
  "navigation",
  "websocket",
  "cache",
  "feed",
  "workbox",
  "general",
];

// Color mapping for categories (for browser console)
const categoryColors: Record<DebugCategory, string> = {
  api: "#3b82f6", // blue
  db: "#10b981", // green
  sync: "#8b5cf6", // purple
  auth: "#f59e0b", // amber
  ui: "#ec4899", // pink
  performance: "#06b6d4", // cyan
  navigation: "#6366f1", // indigo
  websocket: "#14b8a6", // teal
  cache: "#84cc16", // lime
  feed: "#f97316", // orange
  workbox: "#a855f7", // purple
  general: "#6b7280", // gray
};

// Check if we're in a browser environment
function isBrowser(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

// Get storage safely
function getDebugStorage(): Storage | null {
  if (isBrowser()) {
    return window.localStorage;
  }
  return null;
}

// Check if debug is enabled (default: enabled in dev mode)
function isDebugEnabled(): boolean {
  // Check environment variable first
  if (import.meta.env.VITE_DEBUG === "false") {
    return false;
  }

  // Enable by default in development mode
  if (import.meta.env.DEV) {
    return true;
  }

  // Check localStorage override
  const storage = getDebugStorage();
  const override = storage?.getItem("DEBUG_ENABLED");
  if (override === "true") return true;
  if (override === "false") return false;

  return false;
}

// Get enabled categories from localStorage
function getEnabledCategories(): Set<DebugCategory> {
  const storage = getDebugStorage();
  const stored = storage?.getItem("DEBUG_CATEGORIES");

  if (stored) {
    try {
      const categories = JSON.parse(stored) as DebugCategory[];
      return new Set(categories);
    } catch {
      // If parsing fails, return all categories
      return new Set(ALL_CATEGORIES);
    }
  }

  // Default: all categories enabled
  return new Set(ALL_CATEGORIES);
}

// Check if a category is enabled
function isCategoryEnabled(category: DebugCategory): boolean {
  const enabledCategories = getEnabledCategories();
  return enabledCategories.has(category);
}

// Preserve native console methods
const nativeConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  table: console.table?.bind(console),
  group: console.group?.bind(console),
  groupEnd: console.groupEnd?.bind(console),
  time: console.time?.bind(console),
  timeEnd: console.timeEnd?.bind(console),
};

/**
 * Log a debug message with category
 */
export function debugLog(category: DebugCategory, ...args: unknown[]): void {
  if (isDebugEnabled() && isCategoryEnabled(category)) {
    const color = categoryColors[category];
    nativeConsole.log(
      `%c[${category.toUpperCase()}]`,
      `color: ${color}; font-weight: bold;`,
      ...args,
    );
  }
}

/**
 * Log a warning with category
 */
export function debugWarn(category: DebugCategory, ...args: unknown[]): void {
  if (isDebugEnabled() && isCategoryEnabled(category)) {
    const color = categoryColors[category];
    nativeConsole.warn(
      `%c[${category.toUpperCase()}]`,
      `color: ${color}; font-weight: bold;`,
      ...args,
    );
  }
}

/**
 * Log an error with category
 */
export function debugError(category: DebugCategory, ...args: unknown[]): void {
  if (isDebugEnabled() && isCategoryEnabled(category)) {
    const color = categoryColors[category];
    nativeConsole.error(
      `%c[${category.toUpperCase()}]`,
      `color: ${color}; font-weight: bold;`,
      ...args,
    );
  }
}

/**
 * Log a table (for arrays/objects)
 */
export function debugTable(category: DebugCategory, data: unknown): void {
  if (isDebugEnabled() && isCategoryEnabled(category) && nativeConsole.table) {
    const color = categoryColors[category];
    nativeConsole.log(
      `%c[${category.toUpperCase()}]`,
      `color: ${color}; font-weight: bold;`,
    );
    nativeConsole.table(data);
  }
}

/**
 * Start a timer
 */
export function debugTime(category: DebugCategory, label: string): void {
  if (isDebugEnabled() && isCategoryEnabled(category) && nativeConsole.time) {
    nativeConsole.time(`[${category.toUpperCase()}] ${label}`);
  }
}

/**
 * End a timer and log the duration
 */
export function debugTimeEnd(category: DebugCategory, label: string): void {
  if (
    isDebugEnabled() &&
    isCategoryEnabled(category) &&
    nativeConsole.timeEnd
  ) {
    nativeConsole.timeEnd(`[${category.toUpperCase()}] ${label}`);
  }
}

/**
 * Start a collapsible group
 */
export function debugGroup(category: DebugCategory, label: string): void {
  if (isDebugEnabled() && isCategoryEnabled(category) && nativeConsole.group) {
    const color = categoryColors[category];
    nativeConsole.group(
      `%c[${category.toUpperCase()}] ${label}`,
      `color: ${color}; font-weight: bold;`,
    );
  }
}

/**
 * End a collapsible group
 */
export function debugGroupEnd(category: DebugCategory): void {
  if (
    isDebugEnabled() &&
    isCategoryEnabled(category) &&
    nativeConsole.groupEnd
  ) {
    nativeConsole.groupEnd();
  }
}

// Export debugging state
export const debugEnabled = isDebugEnabled();

/**
 * Get all available debug categories
 */
export function getAvailableCategories(): DebugCategory[] {
  return [...ALL_CATEGORIES];
}

/**
 * Set which categories are enabled
 */
export function setEnabledCategories(categories: DebugCategory[]): void {
  const storage = getDebugStorage();
  storage?.setItem("DEBUG_CATEGORIES", JSON.stringify(categories));
}

/**
 * Enable all categories
 */
export function enableAllCategories(): void {
  setEnabledCategories(ALL_CATEGORIES);
}

/**
 * Disable all categories
 */
export function disableAllCategories(): void {
  setEnabledCategories([]);
}

/**
 * Toggle debug mode on/off
 */
export function setDebugEnabled(enabled: boolean): void {
  const storage = getDebugStorage();
  storage?.setItem("DEBUG_ENABLED", enabled ? "true" : "false");
}
